import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";

export default function AreaPerimeterCalculator() {
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [radius, setRadius] = useState("");
  const [base, setBase] = useState("");
  const [height, setHeight] = useState("");
  const [result, setResult] = useState<{ area: number; perimeter: number } | null>(null);

  const calculateRectangle = () => {
    const l = parseFloat(length);
    const w = parseFloat(width);

    if (isNaN(l) || isNaN(w) || l <= 0 || w <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const area = l * w;
    const perimeter = 2 * (l + w);
    setResult({ area, perimeter });

    toast({
      title: "Calculation Complete",
      description: `Area: ${area}, Perimeter: ${perimeter}`,
    });
  };

  const calculateCircle = () => {
    const r = parseFloat(radius);

    if (isNaN(r) || r <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid positive radius.",
        variant: "destructive",
      });
      return;
    }

    const area = Math.PI * r * r;
    const perimeter = 2 * Math.PI * r;
    setResult({ area, perimeter });

    toast({
      title: "Calculation Complete",
      description: `Area: ${area.toFixed(2)}, Circumference: ${perimeter.toFixed(2)}`,
    });
  };

  const calculateTriangle = () => {
    const b = parseFloat(base);
    const h = parseFloat(height);

    if (isNaN(b) || isNaN(h) || b <= 0 || h <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const area = 0.5 * b * h;
    // For perimeter, we'd need all three sides, so we'll just show area
    setResult({ area, perimeter: 0 });

    toast({
      title: "Area Calculated",
      description: `Area: ${area.toFixed(2)}`,
    });
  };

  const reset = () => {
    setLength("");
    setWidth("");
    setRadius("");
    setBase("");
    setHeight("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Area & Perimeter Calculator" 
      calculatorPath="/calculator/area-perimeter"
      category="math"
      subcategory="Geometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Area & Perimeter Calculator</CardTitle>
            <CardDescription>
              Calculate area and perimeter for common shapes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="rectangle">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="rectangle">Rectangle</TabsTrigger>
                <TabsTrigger value="circle">Circle</TabsTrigger>
                <TabsTrigger value="triangle">Triangle</TabsTrigger>
              </TabsList>

              <TabsContent value="rectangle" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="length">Length</Label>
                  <Input
                    id="length"
                    type="number"
                    step="any"
                    value={length}
                    onChange={(e) => setLength(e.target.value)}
                    placeholder="Enter length"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="width">Width</Label>
                  <Input
                    id="width"
                    type="number"
                    step="any"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    placeholder="Enter width"
                  />
                </div>
                <Button onClick={calculateRectangle} className="w-full">Calculate</Button>
                
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-sm">Formulas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <MathFormula block>{String.raw`A = l \times w`}</MathFormula>
                    <MathFormula block>{String.raw`P = 2(l + w)`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="circle" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="radius">Radius</Label>
                  <Input
                    id="radius"
                    type="number"
                    step="any"
                    value={radius}
                    onChange={(e) => setRadius(e.target.value)}
                    placeholder="Enter radius"
                  />
                </div>
                <Button onClick={calculateCircle} className="w-full">Calculate</Button>
                
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-sm">Formulas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <MathFormula block>{String.raw`A = \pi r^2`}</MathFormula>
                    <MathFormula block>{String.raw`C = 2\pi r`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="triangle" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="base">Base</Label>
                  <Input
                    id="base"
                    type="number"
                    step="any"
                    value={base}
                    onChange={(e) => setBase(e.target.value)}
                    placeholder="Enter base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">Height</Label>
                  <Input
                    id="height"
                    type="number"
                    step="any"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="Enter height"
                  />
                </div>
                <Button onClick={calculateTriangle} className="w-full">Calculate Area</Button>
                
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-sm">Formula</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MathFormula block>{String.raw`A = \frac{1}{2}bh`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <Button onClick={reset} variant="outline" className="w-full">Reset All</Button>

            {result && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Results</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-2xl font-bold">Area: {result.area.toFixed(2)} units²</p>
                  {result.perimeter > 0 && (
                    <p className="text-2xl font-bold">Perimeter/Circumference: {result.perimeter.toFixed(2)} units</p>
                  )}
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
